import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  List,
  ListItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { sendChatMessage, ChatMessage } from "../../services/chatApi";
import { sendVoiceMessage } from "../../services/voiceChatApi";

const Chat: React.FC = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSupported, setRecordingSupported] = useState(true);
  const [hasResponseAudio, setHasResponseAudio] = useState(false);
  const listEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastResponseAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (typeof window === "undefined" || !navigator.mediaDevices) {
      setRecordingSupported(false);
      return;
    }
    if (!(window as any).MediaRecorder) {
      setRecordingSupported(false);
    }
  }, []);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setError(null);
    const userMessage: ChatMessage = { role: "user", content: text };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);
    try {
      const nextMessages = [...messages, userMessage];
      const { message } = await sendChatMessage(nextMessages);
      setMessages((prev) => [...prev, message]);
    } catch (err: unknown) {
      const detail =
        axios.isAxiosError(err) && err.response?.data?.detail
          ? String(err.response.data.detail)
          : err instanceof Error
            ? err.message
            : t("chat.connectionError");
      setError(detail);
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/webm",
        });
        stream.getTracks().forEach((track) => track.stop());
        if (rafIdRef.current !== null) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
          analyserRef.current = null;
        }
        if (audioBlob.size === 0) {
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const { transcript, message, response_audio_base64 } =
            await sendVoiceMessage(audioBlob, messages);
          const newMessages: ChatMessage[] = [];
          if (transcript) {
            newMessages.push({ role: "user", content: transcript });
          }
          newMessages.push(message);
          setMessages((prev) => [...prev, ...newMessages]);
          setHasResponseAudio(false);
          if (lastResponseAudioRef.current) {
            lastResponseAudioRef.current.pause();
            lastResponseAudioRef.current = null;
          }
          if (response_audio_base64) {
            try {
              const binary = atob(response_audio_base64);
              const bytes = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i += 1) {
                bytes[i] = binary.charCodeAt(i);
              }
              const blob = new Blob([bytes], { type: "audio/ogg" });
              const url = URL.createObjectURL(blob);
              const audio = new Audio(url);
              lastResponseAudioRef.current = audio;
              audio.onended = () => {
                URL.revokeObjectURL(url);
              };
              setHasResponseAudio(true);
              audio.play().then(
                () => {},
                (playErr: unknown) => {
                  console.warn("Autoplay zablokowany – kliknij „Odtwórz odpowiedź”:", playErr);
                }
              );
            } catch (playErr) {
              console.warn("Odtwarzanie odpowiedzi głosowej nie powiodło się:", playErr);
            }
          }
        } catch (err: unknown) {
          const detail =
            axios.isAxiosError(err) && err.response?.data?.detail
              ? String(err.response.data.detail)
              : err instanceof Error
              ? err.message
              : "Wystąpił błąd podczas przetwarzania nagrania.";
          setError(detail);
        } finally {
          setLoading(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);

       // Proste wykrywanie ciszy po stronie klienta – auto-stop po ~1.5s ciszy
      const AudioCtx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        const audioContext = new AudioCtx();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        source.connect(analyser);
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const silenceThreshold = 0.01; // im mniejsze, tym czulsze
        const maxSilenceMs = 1500;
        let lastNonSilent = performance.now();

        const checkSilence = () => {
          if (!analyserRef.current || !mediaRecorderRef.current) {
            return;
          }
          analyserRef.current.getByteTimeDomainData(dataArray);
          let sumSquares = 0;
          for (let i = 0; i < dataArray.length; i += 1) {
            const v = (dataArray[i] - 128) / 128;
            sumSquares += v * v;
          }
          const rms = Math.sqrt(sumSquares / dataArray.length);
          const now = performance.now();
          if (rms > silenceThreshold) {
            lastNonSilent = now;
          }
          if (
            now - lastNonSilent > maxSilenceMs &&
            mediaRecorderRef.current.state === "recording"
          ) {
            // Auto-stop nagrywania po dłuższej ciszy
            stopRecording();
            return;
          }
          rafIdRef.current = requestAnimationFrame(checkSilence);
        };

        rafIdRef.current = requestAnimationFrame(checkSilence);
      }
    } catch (err) {
      setRecordingSupported(false);
      const detail =
        err instanceof Error
          ? err.message
          : "Nie udało się uzyskać dostępu do mikrofonu.";
      setError(detail);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    setIsRecording(false);
  };

  const handleToggleRecording = () => {
    if (loading || !recordingSupported) return;
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 140px)",
        maxWidth: 800,
        mx: "auto",
      }}
    >
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t("chat.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("chat.description")}
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        <List
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {messages.length === 0 && (
            <ListItem sx={{ justifyContent: "center", color: "text.secondary" }}>
              {t("chat.sendMessageToStart")}
            </ListItem>
          )}
          {messages.map((msg, i) => (
            <ListItem
              key={i}
              sx={{
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  bgcolor:
                    msg.role === "user"
                      ? "primary.main"
                      : "background.default",
                  color: msg.role === "user" ? "primary.contrastText" : "text.primary",
                  borderRadius: 2,
                }}
              >
                {msg.role === "assistant" ? (
                  <Box
                    sx={{
                      fontSize: "0.875rem",
                      lineHeight: 1.5,
                      "& p": { m: 0, mb: 0.5 },
                      "& p:last-child": { mb: 0 },
                      "& ul, & ol": { m: 0, pl: 2.5 },
                      "& li": { mb: 0.25 },
                    }}
                  >
                    <ReactMarkdown>{String(msg.content)}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.content}
                  </Typography>
                )}
              </Paper>
            </ListItem>
          ))}
          {loading && (
            <ListItem sx={{ justifyContent: "flex-start" }}>
              <CircularProgress size={24} />
            </ListItem>
          )}
          {hasResponseAudio && (
            <ListItem sx={{ justifyContent: "flex-start" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<PlayArrowIcon />}
                onClick={() => {
                  const audio = lastResponseAudioRef.current;
                  if (audio) {
                    audio.currentTime = 0;
                    audio.play().catch(() => {});
                  }
                }}
              >
                {t("chat.playResponse")}
              </Button>
            </ListItem>
          )}
          <div ref={listEndRef} />
        </List>
        <Box sx={{ display: "flex", gap: 1, mt: 2, alignItems: "center" }}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("chat.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={loading}
          />
          <Tooltip
            title={
              !recordingSupported
                ? t("chat.recordingUnavailable")
                : isRecording
                ? t("chat.stopAndSend")
                : t("chat.recordQuestion")
            }
          >
            <span>
              <IconButton
                color={isRecording ? "error" : "primary"}
                onClick={handleToggleRecording}
                disabled={loading || !recordingSupported}
                sx={{
                  bgcolor: "background.default",
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                {isRecording ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={loading || !input.trim()}
            endIcon={<SendIcon />}
          >
            {t("chat.send")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Chat;
